// SQLite backup API client (issue #24).
//
// Mirrors `crates/api/src/backups.rs`. Three resource families:
//   - GET / POST `/api/v1/backups`             — list / create
//   - GET / PUT  `/api/v1/backups/settings`    — schedule / retention / dir
//   - GET / DELETE `/api/v1/backups/files/:fn` — download / delete
//
// The download endpoint streams a binary body, not JSON — we trigger
// a browser download via an anchor element so the response actually
// hits disk instead of getting stuffed into memory by `api()`.
import { api } from "@/lib/api";

export interface BackupFile {
  filename: string;
  size_bytes: number;
  modified_at: string | null;
}

export interface ListBackupsResponse {
  directory: string;
  files: BackupFile[];
}

export interface CreateBackupResponse {
  filename: string;
  path: string;
  size_bytes: number;
}

export interface BackupSettings {
  /** Resolved absolute directory (read-only — set via directory_override). */
  directory: string;
  /** Empty string = use the default `<data_dir>/backups/`. */
  directory_override: string;
  /** 0 = retain forever. */
  retention_count: number;
  /** Empty string = schedule disabled. */
  schedule_cron: string;
  default_schedule_cron: string;
  default_retention_count: number;
}

export interface UpdateBackupSettings {
  directory_override?: string;
  retention_count?: number;
  schedule_cron?: string;
}

export const backupsApi = {
  list: () => api<ListBackupsResponse>("/api/v1/backups"),
  create: (gzip = true) =>
    api<CreateBackupResponse>(`/api/v1/backups?gzip=${gzip ? "true" : "false"}`, {
      method: "POST",
    }),
  remove: (filename: string) =>
    api<void>(`/api/v1/backups/files/${encodeURIComponent(filename)}`, {
      method: "DELETE",
    }),
  /**
   * Returns the URL the browser should `window.location.assign(...)` to
   * trigger a download. Works because the GET endpoint sets
   * `Content-Disposition: attachment` and the same-origin session
   * cookie carries the auth context.
   */
  downloadUrl: (filename: string): string =>
    `/api/v1/backups/files/${encodeURIComponent(filename)}`,
  settings: {
    get: () => api<BackupSettings>("/api/v1/backups/settings"),
    update: (body: UpdateBackupSettings) =>
      api<BackupSettings>("/api/v1/backups/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },
};
